import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { Box, Avatar, Icon, Menu, MenuItem, Separator, Button, Badge } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import { badgeLabel, useInboxCounts } from '../lib/inbox';

/**
 * Runner chrome: full color wordmark on desktop, hamburger on mobile,
 * inbox icons and the consumer-style circular account avatar.
 */
const HEADER_NAVY = '#343F5B';
const BADGE_ORANGE = '#F67A1A';
const BADGE_RED = '#EC1C24';
const SIGN_OUT_RED = '#EA4B48';

const badgeSx = (bg: string) => ({
  '& .MuiBadge-badge': { backgroundColor: bg, color: '#fff', fontWeight: 700, fontSize: '11px', minWidth: 18, height: 18, px: '5px' },
});
const headerIconSx = { color: '#fff', '& svg': { color: '#fff' }, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } };

export interface NavLink {
  label: string;
  to: string;
}

export default function MainHeader({ nav, displayName, email, showInbox = false, onMenuOpen, inboxCounts }: { inboxCounts?: { unreadTotal: number; alertUnread: number }; onMenuOpen?: () => void; nav: NavLink[]; displayName?: string | null; email?: string | null; showInbox?: boolean }) {
  const { t, i18n } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [navAnchor, setNavAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const fetchedCounts = useInboxCounts(showInbox && !inboxCounts);
  const { unreadTotal, alertUnread } = inboxCounts ?? fetchedCounts;
  const go = (to: string) => {
    setMenuAnchor(null);
    void navigate(to);
  };

  const onSignOut = async () => {
    setMenuAnchor(null);
    try {
      await signOut();
    } finally {
      window.location.assign('/login');
    }
  };



  return (
    <Box
      component="header"
      data-testid="app-header"
      sx={{
        background: HEADER_NAVY,
        color: '#fff',
        minHeight: 64,
        px: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <Button variant="iconButton" ariaLabel={t('header.menu')} dataTestId="header-nav-menu" startIcon="menu"
        onClick={(e: React.MouseEvent<HTMLElement>) => onMenuOpen ? onMenuOpen() : setNavAnchor(e.currentTarget)}
        sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#fff', '& svg': { color: '#fff' } }} />
      <Box component={Link} to="/" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
        <Box component="img" src={`/brand/runner-color-${i18n.language.startsWith('es') ? 'es' : 'en'}.png`} alt={t('runnerNav.logo')} sx={{ width: 190, height: 44, objectFit: 'contain' }} />
      </Box>
      <Box sx={{ flex: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Menu anchorEl={navAnchor} open={Boolean(navAnchor)} onClose={() => setNavAnchor(null)}
          items={nav.map((n) => ({ value: n.to, label: n.label, onClick: () => { setNavAnchor(null); void navigate(n.to); } }))} />
        {showInbox && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Badge badgeContent={unreadTotal} max={99} sx={badgeSx(BADGE_ORANGE)}>
              <Button
                variant="iconButton"
                ariaLabel={unreadTotal > 0 ? t('header.messagesUnread', { count: unreadTotal, label: badgeLabel(unreadTotal) }) : t('header.messages')}
                dataTestId="header-messages"
                startIcon={<Box component="img" src="/brand/navigation/header-mail.svg" alt="" sx={{ width: 24, height: 24 }} />}
                onClick={() => go('/messages')}
                sx={headerIconSx}
              />
            </Badge>
            <Badge badgeContent={alertUnread} max={99} sx={badgeSx(BADGE_RED)}>
              <Button
                variant="iconButton"
                ariaLabel={alertUnread > 0 ? t('header.alertsUnread', { count: alertUnread, label: badgeLabel(alertUnread) }) : t('header.alerts')}
                dataTestId="header-alerts"
                startIcon={<Box component="img" src="/brand/navigation/header-bell.svg" alt="" sx={{ width: 24, height: 24 }} />}
                onClick={() => go('/messages?alerts=1')}
                sx={headerIconSx}
              />
            </Badge>
          </Box>
        )}
        <Box
          component="button"
          type="button"
          data-testid="header-account-menu"
          aria-label={t('header.accountMenu')}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)}
          sx={{ all: 'unset', cursor: 'pointer', ml: 1, display: 'flex', width: 44, height: 44, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } }}
        >
          <Avatar ariaLabel="" fallbackText={displayName ?? email ?? 'Runner'} size="small" bgColor="#94A3A0" />
        </Box>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          aria-label={t('header.accountMenu')}
          minWidth={220}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem value="settings" icon="settings" onClick={() => go('/account/overview')}>
            <span data-testid="header-menu-settings">{t('header.settings')}</span>
          </MenuItem>
          <MenuItem value="kit" icon="autoStoriesOutlined" onClick={() => go('/kit')}>
            <span data-testid="header-menu-kit">{t('header.kit')}</span>
          </MenuItem>
          <MenuItem value="handbook" icon="menuBook" onClick={() => go('/handbook')}>
            <span data-testid="header-menu-handbook">{t('header.handbook')}</span>
          </MenuItem>
          <MenuItem value="help" icon="helpCenter" onClick={() => go('/help')}>
            <span data-testid="header-menu-help">{t('header.help')}</span>
          </MenuItem>
          <MenuItem value="lang" icon="language" onClick={() => { setMenuAnchor(null); void i18n.changeLanguage(i18n.language.startsWith('es') ? 'en' : 'es'); }}>
            <span data-testid="header-menu-language">{t('header.language')} · {t('lang.toggle')}</span>
          </MenuItem>
          <Separator spacing="4px" />
          <MenuItem value="signout" icon={<Icon name="logout" size={20} color={SIGN_OUT_RED} ariaLabel="" />} onClick={() => void onSignOut()} sx={{ color: SIGN_OUT_RED, fontWeight: 700 }}>
            <span data-testid="header-menu-signout">{t('header.signOut')}</span>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}

